/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box, Paper, Popover } from "@mui/material";
import { DifferencePanel } from "components/search/input/DifferencePanel/DifferencePanel";
import { useState, useEffect } from "react";
import { useGetNameBasedSearchDiffQuery } from "store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { useDiffDisplayContext } from "components/search/input/DiffDisplayContext";

type Props = React.PropsWithChildren<{
  sourceSearchId?: number;
  targetSearchId: number;
}>;

export const DifferencePanelPopover = ({
  sourceSearchId,
  targetSearchId,
  children,
}: Props) => {
  const { setActive, clear } = useDiffDisplayContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const tenantName = useTenant();

  const diffQuery = useGetNameBasedSearchDiffQuery(
    {
      tenantName,
      targetSearchId: targetSearchId,
      sourceSearchId: sourceSearchId!,
    },
    {
      skip: !sourceSearchId || !anchorEl,
      refetchOnMountOrArgChange: true,
    }
  );

  // Force popover to reposition when data loads
  useEffect(() => {
    if (anchorEl && diffQuery.isSuccess) {
      // Small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        const event = new Event("resize");
        window.dispatchEvent(event);
      }, 0);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [anchorEl, diffQuery.isSuccess]);

  useEffect(() => {
    if (anchorEl) {
      setActive(targetSearchId, sourceSearchId);
      return clear; // destructor to clear active search
    }
  }, [anchorEl, targetSearchId, sourceSearchId, clear, setActive]);

  if (sourceSearchId === undefined) return children;

  return (
    <Box
      onMouseEnter={(e) => {
        if (sourceSearchId) setAnchorEl(e.currentTarget);
      }}
      onMouseLeave={() => {
        setAnchorEl(null);
      }}
    >
      {children}
      <Popover
        key={diffQuery.isSuccess ? "loaded" : "loading"}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: "none",
          "& .MuiPopover-paper": {
            pointerEvents: "auto",
            overflow: "auto",
            ml: 1,
          },
        }}
      >
        <Paper sx={{ m: 0, p: 0 }}>
          <DifferencePanel
            targetSearchId={targetSearchId}
            sourceSearchId={sourceSearchId}
          />
        </Paper>
      </Popover>
    </Box>
  );
};
